/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { ITaskContainer, ISmartTaskBinding } from 'app/core/components/tasks/task';
import { CaseService } from 'app/cm/services/case-service';
import { IServiceSearchFilters, LookupService } from 'app/cd/services/lookup-service';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { parseCaseServiceDefaults } from 'app/cm/lib/utilities';

@autoinject()
export class SmartService extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected memberId: string | null = null;
    protected caseId: string | null = null;

    protected task!: Core.Task;
    protected targetId!: string;
    protected tasksConfig: ITasksConfig = {};
    protected readonly: boolean = false;
    protected caseService!: CM.CaseService;
    protected editReason: string | null = null;

    private container!: ITaskContainer;
    protected $isValid!: boolean;
    protected validate = validate;

    protected defaultService: CD.Service | null = null;
    protected defaultModifier: CD.ClaimModifier | null = null;
    protected defaultPlaceOfService: CD.PlaceOfService | null = null;

    ////****************** Element References ***************************************////
    protected $form!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: CaseService,
        private readonly lookupService: LookupService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }


    protected async activate(params: ISmartTaskBinding): Promise<void> {
        if(!params || !params.container || !params.targetId || !params.task || !params.task.id || !params.tasksConfig || !params.tasksConfig.smartTasksConfig || !params.tasksConfig.smartTasksConfig.notesConfig) {
            throw TypeError(`${SmartService.name} Component Model not Bound.`);
        }

        this.targetId = params.targetId;
        this.tasksConfig = params.tasksConfig;
        this.memberId = params.relatedModels && params.relatedModels['CD.Member'] || null;
        this.caseId = params.relatedModels && params.relatedModels['CM.Case'] || null;

        this.state = params.state || this.state;

        this.task = params.task;
        this.container = params.container;
        this.readonly = params.readonly || (this.task.isCompleted || false);
    }

    protected async bind(): Promise<void> {
        this.checkPermissions();
        await this.loadDefaults();
    }


    ////****************** Events ***************************************************////
    protected async addServiceSuccess(): Promise<void> {
        if(this.container) {
            if(await this.container.completeTask(true)) {
                this.loadCaseService(this.caseId, this.task?.id);
                this.task.isCompleted = true;
                this.readonly = true;
            }
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadDefaults(): Promise<void> {

        await this.doLoad(async () => {
            const defaultValues = parseCaseServiceDefaults(this.task?.description || '');

            const serviceSearchFilters: IServiceSearchFilters = { isDental: false }; // exclude dental services
            const [services, modifiers, placesOfService, caseService] = await Promise.all(
                [
                    this.lookupService.searchServices(defaultValues.serviceCode, {}, [], false, serviceSearchFilters),
                    this.lookupService.loadClaimModifiers(defaultValues.modifierCode),
                    this.lookupService.loadPlacesOfService(defaultValues.placeOfServiceCode),
                    this.loadCaseService(this.caseId, this.task?.id)

                ]
            );

            if(services.results.length >= 1 && defaultValues.serviceCode) {
                this.defaultService = services.results[0];
                this.caseService.service = this.defaultService;
                if(services.results.length > 1) this.logger.warn(`Smart Service Task - Multiple services found for default service code: ${defaultValues.serviceCode}`);
            }
            if(modifiers.results.length >= 1 && defaultValues.modifierCode) {
                this.defaultModifier = modifiers.results[0];
                this.caseService.modifier = this.defaultModifier;
                if(modifiers.results.length > 1) this.logger.warn(`Smart Service Task - Multiple modifiers found for default modifier code: ${defaultValues.modifierCode}`);
            }

            if(placesOfService.results.length >= 1 && defaultValues.placeOfServiceCode) {
                this.defaultPlaceOfService = placesOfService.results[0];
                this.caseService.placeOfService = this.defaultPlaceOfService;
                if(placesOfService.results.length > 1) this.logger.warn(`Smart Service Task - Multiple places of service found for default place of service code: ${defaultValues.placeOfServiceCode}`);
            }
        });
    }

    @apiCall
    private async loadCaseService(caseId?: string | null, taskId?: string | null | undefined): Promise<void> {
        await this.doLoad(async () => {
            if(!caseId || !taskId) {
                return;
            }

            const service = await this.service.loadCaseService(caseId, taskId, ['service', 'modifier', 'placeOfService', 'quantity.unit', 'history']);

            if(service && service.results.length === 1) {
                this.caseService = service.results[0];
            }
            else {
                this.caseService = {}
            }

            if(this.caseService.history && this.caseService.history?.length > 0) {
                const historyLength = this.caseService.history?.length
                this.editReason = this.caseService.history[historyLength - 1].reason || ''
            }

        });
    }
}
